import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters, periodToDates } from "../lib/filters";
import { PageHeader } from "../components/PageHeader";
import { PageSkeleton } from "../components/LoadingSkeleton";
import { EmptyState } from "../components/EmptyState";
import {
  IconLightbulb, IconRefresh, IconAlertTriangle, IconCheck, IconInfo, IconArrowUp,
} from "../components/icons";
import type { IntelligenceDto, IntelItem, IntelSeverity, IntelArea } from "@bi/types";
import { useQueryClient } from "@tanstack/react-query";

type AreaFilter = "all" | IntelArea;

const AREAS: Array<{ value: AreaFilter; label: string }> = [
  { value: "all",         label: "Todas" },
  { value: "financeiro",  label: "Financeiro" },
  { value: "operacional", label: "Operacional CT-e" },
  { value: "dre",         label: "DRE / Resultado" },
  { value: "fluxo_caixa", label: "Fluxo de Caixa" },
  { value: "frota",       label: "Frota" },
];

const SEVERITY_META: Record<IntelSeverity, { label: string; chip: string; card: string; icon: React.ComponentType<{ className?: string }> }> = {
  critical:    { label: "Crítico",     chip: "bg-danger-50 text-danger-700 border-danger-200",  card: "border-l-danger-500",  icon: IconAlertTriangle },
  warning:     { label: "Atenção",     chip: "bg-amber-50 text-amber-700 border-amber-200",     card: "border-l-amber-500",   icon: IconAlertTriangle },
  opportunity: { label: "Oportunidade", chip: "bg-tm-50 text-tm-700 border-tm-200",             card: "border-l-tm-500",      icon: IconArrowUp },
  info:        { label: "Informativo", chip: "bg-info-50 text-info-700 border-info-100",         card: "border-l-info-500",    icon: IconInfo },
};

const SEVERITY_ORDER: IntelSeverity[] = ["critical", "warning", "opportunity", "info"];

export function IntelligencePage() {
  const qc = useQueryClient();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const [area, setArea] = useState<AreaFilter>("all");
  const [severity, setSeverity] = useState<IntelSeverity | "all">("all");

  const { data, isLoading, error } = useQuery({
    queryKey: ["intelligence", from, to, filters.comparison],
    queryFn: () =>
      api.get<IntelligenceDto>(
        `/dashboards/transport/intelligence?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["intelligence"] });

  if (isLoading) return <PageSkeleton />;

  if (error || !data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Insights e Alertas" subtitle="Inteligência sobre receita, custos, inadimplência e caixa" />
        <EmptyState
          icon={<IconLightbulb className="w-7 h-7" />}
          title="Não foi possível carregar a inteligência"
          description={(error as Error)?.message ?? "Tente novamente em instantes."}
        />
      </div>
    );
  }

  // Filtragem por área + severidade (área também filtra no backend, mas mantemos
  // client-side para a troca ser instantânea com os dados já carregados).
  const items = data.items.filter((i) =>
    (area === "all" || i.area === area) &&
    (severity === "all" || i.severity === severity),
  );

  const hasCritical = data.items.some((i) => i.severity === "critical");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Insights e Alertas"
        subtitle="Inteligência sobre receita, custos, inadimplência e caixa — calculada com dados reais do período"
        actions={
          <button onClick={refresh} className="btn-secondary">
            <IconRefresh className="w-4 h-4" />
            Atualizar
          </button>
        }
      />

      {/* ── Avisos de dado parcial ──────────────────────────────────────────── */}
      {data.warnings.length > 0 && (
        <div className="rounded-xl border border-warning-200 bg-warning-50 px-5 py-4 space-y-1.5">
          {data.warnings.map((w, i) => (
            <p key={i} className="text-sm text-warning-700 flex items-start gap-2">
              <IconInfo className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {w}
            </p>
          ))}
        </div>
      )}

      {/* ── Resumo por severidade ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {SEVERITY_ORDER.map((sev) => {
          const meta = SEVERITY_META[sev];
          const count = data.counts[sev];
          const active = severity === sev;
          return (
            <button
              key={sev}
              onClick={() => setSeverity(active ? "all" : sev)}
              className={`card p-4 text-left transition-all hover:shadow-card-md ${active ? "ring-2 ring-tm-400" : ""}`}
            >
              <p className="text-2xl font-bold text-gray-900">{count}</p>
              <div className="mt-1.5 inline-flex items-center gap-1.5">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${meta.chip}`}>
                  <meta.icon className="w-3 h-3" />
                  {meta.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Filtro por área ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500">Área:</span>
        {AREAS.map((a) => (
          <button
            key={a.value}
            onClick={() => setArea(a.value)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              area === a.value ? "bg-tm-500 text-white" : "bg-surface-muted text-gray-600 hover:bg-surface-border"
            }`}
          >
            {a.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-400">{items.length} itens</span>
      </div>

      {/* ── Estado positivo (sem críticos) ──────────────────────────────────── */}
      {!hasCritical && severity === "all" && area === "all" && (
        <div className="flex items-center gap-3 rounded-xl border border-tm-200 bg-tm-50 px-5 py-4">
          <span className="w-9 h-9 rounded-full bg-tm-100 grid place-items-center flex-shrink-0">
            <IconCheck className="w-5 h-5 text-tm-600" />
          </span>
          <div>
            <p className="text-sm font-semibold text-tm-800">Nenhum alerta crítico encontrado no período.</p>
            <p className="text-xs text-tm-600 mt-0.5">Os indicadores abaixo são pontos de atenção e oportunidades.</p>
          </div>
        </div>
      )}

      {/* ── Lista de itens ──────────────────────────────────────────────────── */}
      {items.length === 0 ? (
        <EmptyState
          icon={<IconLightbulb className="w-7 h-7" />}
          title="Nenhum item nesta seleção"
          description="Ajuste os filtros de área ou severidade para ver outros insights e alertas."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((it) => (
            <IntelItemCard key={it.id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function IntelItemCard({ item }: { item: IntelItem }) {
  const meta = SEVERITY_META[item.severity];
  return (
    <div className={`card border-l-4 ${meta.card} p-5 space-y-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${meta.chip}`}>
              <meta.icon className="w-3 h-3" />
              {meta.label}
            </span>
            <span className="text-xs text-gray-400">{item.category}</span>
          </div>
          <p className="mt-2 text-sm font-semibold text-gray-900">{item.title}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-bold text-gray-900 whitespace-nowrap">{item.mainValue}</p>
          {item.comparison && <p className="text-xs text-gray-400 mt-0.5">{item.comparison}</p>}
        </div>
      </div>

      <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>

      {item.recommendation && (
        <div className="flex items-start gap-2 text-xs text-gray-600 bg-surface-muted rounded-lg px-3 py-2 border-l-2 border-tm-300">
          <IconLightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-tm-500" />
          <span><span className="font-semibold">Recomendação:</span> {item.recommendation}</span>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-400 pt-1">
        <span>Período: {item.periodLabel}</span>
        {item.source && <span className="truncate ml-2">Fonte: {item.source}</span>}
      </div>
    </div>
  );
}
